import { IFBCallData } from "@sdk/fb-call-logs/fb-call-logs-model";
import { GlobalConfig } from "config";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  startCallRecording,
  type CallRecordingHandle,
} from "../utils/call-recording";

export interface StartRecordingConfig {
  organizationId: string;
  conversationId: string;
  callId: string;
  userId: string;
  engineUrl?: string;
}

interface UseWebRTCIncomingFBCallReturn {
  peerConnection: RTCPeerConnection | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  isMuted: boolean;
  isSpeakerOn: boolean;
  /** Creates local SDP **offer** for Messenger C2B accept (not an answer). */
  acceptCall: () => Promise<string>;
  /** Apply Meta's SDP answer from `/facebook/accept-call` response. */
  applyRemoteAnswer: (sdpAnswer: string) => Promise<void>;
  rejectCall: () => Promise<void>;
  terminateCall: () => Promise<void>;
  toggleMute: () => void;
  toggleSpeaker: () => void;
  setAudioInputDevice: (deviceId: string) => Promise<void>;
  setAudioOutputDevice: (deviceId: string) => Promise<void>;
  audioInputDevices: MediaDeviceInfo[];
  audioOutputDevices: MediaDeviceInfo[];
  selectedAudioInput: string | null;
  selectedAudioOutput: string | null;
  callDuration: number;
  isProcessing: boolean;
  isRecording: boolean;
}

/**
 * Messenger C2B (inbound): business creates an SDP **offer** on accept;
 * Meta returns an SDP **answer** that must be applied as remote description.
 * (Opposite of WhatsApp Cloud Native, which answers a remote offer.)
 */
export const useWebRTCIncomingFBCall = (
  _callData: IFBCallData,
  recordingConfig?: StartRecordingConfig | null,
): UseWebRTCIncomingFBCallReturn => {
  const [peerConnection, setPeerConnection] =
    useState<RTCPeerConnection | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [audioInputDevices, setAudioInputDevices] = useState<MediaDeviceInfo[]>(
    [],
  );
  const [audioOutputDevices, setAudioOutputDevices] = useState<
    MediaDeviceInfo[]
  >([]);
  const [selectedAudioInput, setSelectedAudioInput] = useState<string | null>(
    null,
  );
  const [selectedAudioOutput, setSelectedAudioOutput] = useState<string | null>(
    null,
  );

  const durationIntervalRef = useRef<number | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const recordingHandleRef = useRef<CallRecordingHandle | null>(null);
  const recordingConfigRef = useRef<StartRecordingConfig | null | undefined>(
    recordingConfig,
  );
  const [isRecording, setIsRecording] = useState(false);

  recordingConfigRef.current = recordingConfig;

  const rtcConfiguration: RTCConfiguration = useMemo(
    () => ({
      iceServers: [{ urls: "stun:stun.relay.metered.ca:80" }],
    }),
    [],
  );

  const getAudioDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices.filter((device) => device.kind === "audioinput");
      const outputs = devices.filter((device) => device.kind === "audiooutput");

      setAudioInputDevices(inputs);
      setAudioOutputDevices(outputs);

      if (inputs.length > 0 && !selectedAudioInput && inputs[0].deviceId) {
        setSelectedAudioInput(inputs[0].deviceId);
      }
      if (outputs.length > 0 && !selectedAudioOutput && outputs[0].deviceId) {
        setSelectedAudioOutput(outputs[0].deviceId);
      }
    } catch (error) {
      console.error("Error getting audio devices:", error);
    }
  }, [selectedAudioInput, selectedAudioOutput]);

  const getUserMedia = useCallback(async (audioDeviceId?: string) => {
    try {
      const constraints: MediaStreamConstraints = {
        audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true,
      };
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (error) {
      console.error("Error getting user media:", error);
      throw error;
    }
  }, []);

  const startRecordingInternal = useCallback(
    async (
      config: StartRecordingConfig,
    ): Promise<CallRecordingHandle | null> => {
      const local = localStreamRef.current;
      const remote = remoteStreamRef.current;
      if (!local || !remote) return null;
      if (recordingHandleRef.current) return recordingHandleRef.current;
      try {
        const handle = await startCallRecording(local, remote, {
          ...config,
          engineUrl: config.engineUrl ?? GlobalConfig.MEDIA_RECORDER_ENGINE_URL,
        });
        recordingHandleRef.current = handle;
        setIsRecording(true);
        return handle;
      } catch (err) {
        console.error("Failed to start recording:", err);
        return null;
      }
    },
    [],
  );

  const stopRecordingInternal = useCallback(async (): Promise<void> => {
    if (recordingHandleRef.current) {
      await recordingHandleRef.current.stop();
      recordingHandleRef.current = null;
    }
    setIsRecording(false);
  }, []);

  // Messenger C2B: create SDP **offer** (do not set remote yet — answer comes from API)
  const acceptCall = useCallback(async (): Promise<string> => {
    setIsProcessing(true);
    try {
      await stopRecordingInternal();
      console.log("Starting Messenger inbound accept (SDP offer)...");

      const pc = new RTCPeerConnection(rtcConfiguration);

      const localStream = await getUserMedia(selectedAudioInput || undefined);
      if (pc.signalingState === "closed") {
        localStream.getTracks().forEach((t) => t.stop());
        throw new Error("PeerConnection was closed before adding tracks");
      }

      localStreamRef.current = localStream;
      localStream.getTracks().forEach((track) => {
        pc.addTrack(track, localStream);
      });

      pc.ontrack = (event) => {
        console.log("Received audio track from Messenger");
        remoteStreamRef.current = event.streams[0];
        const config = recordingConfigRef.current;
        if (
          config &&
          localStreamRef.current &&
          event.streams[0] &&
          !recordingHandleRef.current
        ) {
          startRecordingInternal(config).catch((err) =>
            console.error("Auto-start recording failed:", err),
          );
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      pcRef.current = pc;
      setPeerConnection(pc);

      durationIntervalRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);

      return offer.sdp || "";
    } catch (error) {
      console.error("Error creating Messenger accept offer:", error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [
    selectedAudioInput,
    getUserMedia,
    startRecordingInternal,
    stopRecordingInternal,
    rtcConfiguration,
  ]);

  const applyRemoteAnswer = useCallback(async (sdpAnswer: string) => {
    const pc = pcRef.current;
    if (!pc || !sdpAnswer) {
      throw new Error("No peer connection or SDP answer to apply");
    }
    if (pc.remoteDescription) {
      return;
    }
    await pc.setRemoteDescription(
      new RTCSessionDescription({
        type: "answer",
        sdp: sdpAnswer,
      }),
    );
    console.log("Remote description set with Messenger answer");
  }, []);

  const rejectCall = useCallback(async (): Promise<void> => {
    setIsProcessing(true);
    try {
      await stopRecordingInternal();
      if (pcRef.current) {
        pcRef.current.close();
        setPeerConnection(null);
        pcRef.current = null;
      }

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
        localStreamRef.current = null;
      }

      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }

      setCallDuration(0);
      remoteStreamRef.current = null;
    } catch (error) {
      console.error("Error rejecting call:", error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [stopRecordingInternal]);

  const terminateCall = useCallback(async (): Promise<void> => {
    return rejectCall();
  }, [rejectCall]);

  const toggleMute = useCallback(() => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  }, []);

  const toggleSpeaker = useCallback(() => {
    const newSpeakerState = !isSpeakerOn;
    setIsSpeakerOn(newSpeakerState);

    try {
      const audioElements = document.querySelectorAll("audio");
      audioElements.forEach((audio) => {
        if (audio.srcObject) {
          if (newSpeakerState) {
            audio.muted = false;
            audio.volume = 1.0;
            if ("sinkId" in audio && selectedAudioOutput) {
              (audio as any).setSinkId(selectedAudioOutput);
            }
          } else {
            audio.muted = true;
          }
        }
      });
    } catch (error) {
      console.warn("Error toggling speaker:", error);
    }
  }, [isSpeakerOn, selectedAudioOutput]);

  const setAudioInputDevice = useCallback(
    async (deviceId: string) => {
      try {
        setSelectedAudioInput(deviceId);

        if (localStreamRef.current) {
          const currentTrack = localStreamRef.current.getAudioTracks()[0];
          const wasMuted = currentTrack ? !currentTrack.enabled : false;

          if (currentTrack) {
            currentTrack.stop();
          }

          const newStream = await getUserMedia(deviceId);

          if (pcRef.current) {
            const senders = pcRef.current.getSenders();
            const audioSender = senders.find(
              (sender) => sender.track?.kind === "audio",
            );
            if (audioSender) {
              const newTrack = newStream.getAudioTracks()[0];
              await audioSender.replaceTrack(newTrack);
            }
          }

          const newTrack = newStream.getAudioTracks()[0];
          if (newTrack && wasMuted) {
            newTrack.enabled = false;
          }

          localStreamRef.current = newStream;
        }
      } catch (error) {
        console.error("Error setting audio input device:", error);
      }
    },
    [getUserMedia],
  );

  const setAudioOutputDevice = useCallback(
    async (deviceId: string) => {
      try {
        setSelectedAudioOutput(deviceId);

        const audioElements = document.querySelectorAll("audio");
        audioElements.forEach((audio) => {
          if ("sinkId" in audio) {
            (audio as any).setSinkId(deviceId);
          }
        });

        if (isSpeakerOn) {
          audioElements.forEach((audio) => {
            if (audio.srcObject) {
              audio.muted = false;
              audio.volume = 1.0;
            }
          });
        }
      } catch (error) {
        console.error("Error setting audio output device:", error);
      }
    },
    [isSpeakerOn],
  );

  useEffect(() => {
    getAudioDevices();
    navigator.mediaDevices.addEventListener("devicechange", getAudioDevices);
    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        getAudioDevices,
      );
    };
  }, [getAudioDevices]);

  useEffect(() => {
    const config = recordingConfigRef.current;
    if (!config) return;
    const local = localStreamRef.current;
    const remote = remoteStreamRef.current;
    if (!local || !remote || recordingHandleRef.current) return;
    startRecordingInternal(config).catch((err) =>
      console.error("Auto-start recording (effect) failed:", err),
    );
  }, [recordingConfig, startRecordingInternal]);

  useEffect(() => {
    return () => {
      stopRecordingInternal().catch((err) =>
        console.error("Error stopping recording:", err),
      );
      if (pcRef.current) {
        pcRef.current.close();
      }
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      try {
        const audioElements = document.querySelectorAll("audio");
        audioElements.forEach((audio) => {
          if (audio.srcObject && audio.parentNode) {
            try {
              audio.parentNode.removeChild(audio);
            } catch {
              audio.srcObject = null;
              audio.remove();
            }
          }
        });
      } catch (error) {
        console.warn("Error removing audio elements:", error);
      }
    };
  }, [stopRecordingInternal]);

  return {
    peerConnection,
    localStream: localStreamRef.current,
    remoteStream: remoteStreamRef.current,
    isMuted,
    isSpeakerOn,
    acceptCall,
    applyRemoteAnswer,
    rejectCall,
    terminateCall,
    toggleMute,
    toggleSpeaker,
    setAudioInputDevice,
    setAudioOutputDevice,
    audioInputDevices,
    audioOutputDevices,
    selectedAudioInput,
    selectedAudioOutput,
    callDuration,
    isProcessing,
    isRecording,
  };
};
